import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { BadgeTypeConstants, ColorConstants, ProjectIssuanceConstants } from "../../../../../constants";
import { BadgeData } from "../../../../../models";
import {
  GetProjectDetailsResponse,
  getProjectIssuances,
  getStandardSpecificSchemaDetails,
} from "../../../../../service/publicquery";
import { Status } from "../../../../../service/Shared";
import { jsonReviver } from "../../../../../utils";
import { StackedChartData } from "../../../../../widget";
import { StandardSpecificDefinition } from "../../../../shared/projects/project/overview/components/StandardSpecificSection/types";
import { usePublicContext } from "../../../usePublicContext";

interface UsePreviewReturnData {
  isLoading: boolean;
  projectDetails: GetProjectDetailsResponse | undefined;
  tags: string[] | null;
  badges: BadgeData[];
  certifications: BadgeData[];
  stackedChartData: StackedChartData[] | undefined;
  xAxisLabels: string[];
  standardSpecificDefinition: StandardSpecificDefinition | undefined;
}

export const usePreview = (): UsePreviewReturnData => {
  const { uuid } = useParams();
  const { projectDetails } = usePublicContext();
  const [isLoading, setIsLoading] = useState(true);
  const [standardSpecificDefinition, setStandardSpecificDefinition] = useState<
    StandardSpecificDefinition | undefined
  >();

  const [stackedChartData, setStackedChartData] = useState<StackedChartData[]>([]);
  const [xAxisLabels, setXAxisLabels] = useState<string[]>([]);

  const tags = !!projectDetails?.tags && !!projectDetails?.tags.length ? projectDetails?.tags : null;
  const certifications =
    projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_CERTIFICATION) || [];
  const badges = projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_BADGE) || [];

  const fetchData = useCallback(async (projectUuid: string) => {
    const response = await getProjectIssuances({
      projectUuid,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 30,
      },
    });

    if (response.status === Status.Success && response.data) {
      const keys: string[] = [];
      const verifiedUnits: number[] = [];
      const pendingUnits: number[] = [];

      const results = response.data?.results ?? [];

      results.forEach((value) => {
        const endYear = JSON.parse(value.standardSpecificIssuanceData, jsonReviver)?.vintageEndDate?.getFullYear();
        const period = endYear?.toString();

        // Avoid duplicate keys
        if (!keys.includes(period)) {
          const total = results
            .filter(
              (d) => JSON.parse(d.standardSpecificIssuanceData, jsonReviver)?.vintageEndDate?.getFullYear() <= endYear
            )
            .reduce((acc: number, k) => acc + k.unitQuantityTotal, 0);

          keys.push(period);
          verifiedUnits.push(total);
          pendingUnits.push(projectDetails?.cachedTotalQuantity ?? 0 - total);
        }
      });

      const unitTypes =
        projectDetails?.standard.uuid !== undefined
          ? ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_BY_STANDARD[projectDetails?.standard.uuid]
          : undefined;

      setXAxisLabels(keys);
      setStackedChartData([
        {
          label:
            (unitTypes !== undefined
              ? ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_LONG_HAND[unitTypes[0]]
              : undefined) ?? "Verified carbon units (VCUs)",
          data: verifiedUnits,
          backgroundColor: ColorConstants.INDIGO_500,
          hoverBackgroundColor: ColorConstants.INDIGO_400,
        },
        {
          label:
            (unitTypes !== undefined
              ? ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_LONG_HAND[unitTypes[1]]
              : undefined) ?? "Pending issuance units (PIUs)",
          data: pendingUnits,
          backgroundColor: ColorConstants.INDIGO_200,
          hoverBackgroundColor: ColorConstants.INDIGO_100,
        },
      ]);
    }

    if (projectDetails && projectDetails.standardSpecificSchemaUuid) {
      const standardDefinitionResponse = await getStandardSpecificSchemaDetails({
        standardSpecificSchemaUuid: projectDetails.standardSpecificSchemaUuid,
      });

      if (standardDefinitionResponse.status === Status.Success && standardDefinitionResponse.data) {
        setStandardSpecificDefinition(standardDefinitionResponse.data.definition);
      }
    }
  }, []);

  useEffect(() => {
    if (uuid) {
      fetchData(uuid).then(() => setIsLoading(false));
    }
  }, [fetchData]);

  return {
    isLoading,
    stackedChartData,
    xAxisLabels,
    tags,
    badges,
    certifications,
    projectDetails,
    standardSpecificDefinition,
  };
};
